import { useEffect, useState } from "react";

// A minimal hand-rolled client-side router (History API pushState/popstate +
// a module-level pub-sub), consistent with this codebase's "no framework
// unless a milestone needs it" approach (no Gin, no chart library). The
// register table needs shareable/bookmarkable filtered URLs (spec §9), so
// filter state is designed to live in the query string from the start —
// later readers (M7-M11) reuse this same router rather than each inventing
// their own navigation.

type Listener = () => void;
const listeners = new Set<Listener>();

export interface Location {
  pathname: string;
  search: string;
}

function currentLocation(): Location {
  return { pathname: window.location.pathname, search: window.location.search };
}

// navigate pushes (or replaces) a new URL and notifies every mounted
// useLocation() subscriber. replace is for high-frequency updates (e.g. a
// search box keystroke syncing into the query string) that shouldn't spam
// browser history; actual navigation (clicking a row, a nav link) should
// push so the back button works as expected.
export function navigate(path: string, opts: { replace?: boolean } = {}) {
  if (opts.replace) {
    window.history.replaceState(null, "", path);
  } else {
    window.history.pushState(null, "", path);
  }
  listeners.forEach((l) => l());
}

export function useLocation(): Location {
  const [loc, setLoc] = useState<Location>(currentLocation);

  useEffect(() => {
    const update = () => setLoc(currentLocation());
    listeners.add(update);
    window.addEventListener("popstate", update);
    // Catch up on any navigation that happened between initial state and
    // this effect's mount (React 18 strict-mode double-invoke safe: this is
    // idempotent).
    update();
    return () => {
      listeners.delete(update);
      window.removeEventListener("popstate", update);
    };
  }, []);

  return loc;
}

// GLOBAL_SECTIONS are routes with no repo prefix at all — /search searches
// across every configured repo, so it's never scoped to one. Checked before
// treating a path's first segment as a repo key.
const GLOBAL_SECTIONS = new Set(["search"]);

export interface Route {
  repo: string | null; // null only for a global section (see GLOBAL_SECTIONS) or "/" itself
  section: string | null; // null = the repo's own dashboard
  id: string | null;
}

// parseRoute is the single parser every route-consuming component uses,
// replacing what used to be five independent matchDetail() calls in
// App.tsx. Grammar: "/search" (global, no repo) | "/{repo}" (dashboard) |
// "/{repo}/{section}" | "/{repo}/{section}/{id}". A bare pre-multi-repo
// bookmark like "/adrs" parses as repo="adrs" here — App.tsx is
// responsible for detecting that shape (against its own closed list of
// section names) and redirecting before treating the parsed result as real,
// since this function has no way to know which segment-1 strings are
// section names versus real repo keys.
export function parseRoute(pathname: string): Route {
  if (GLOBAL_SECTIONS.has(pathname.replace(/^\//, ""))) {
    return { repo: null, section: pathname.replace(/^\//, ""), id: null };
  }
  const [repo, section, id] = pathname.split("/").filter(Boolean);
  return {
    repo: repo ?? null,
    section: section ?? null,
    id: id !== undefined ? decodeURIComponent(id) : null,
  };
}

// repoPath builds a repo-scoped path — the write-side counterpart to
// parseRoute, used by every navigate()/href call site in the app instead of
// hand-building "/${repo}/${section}/${id}" strings. Deliberately not
// folded into navigate() itself: capabilityFilters.ts's URL-sync calls
// navigate() with a query-string-only path that repoPath doesn't need to
// (and shouldn't) touch.
export function repoPath(repo: string, section?: string, id?: string): string {
  let path = `/${repo}`;
  if (section) path += `/${section}`;
  if (id !== undefined) path += `/${encodeURIComponent(id)}`;
  return path;
}

// useCurrentRepo reads the repo segment out of the current URL. Falls back
// to "atlas" — the only repo this app knows about until GET /api/repos
// lands (a later PR) — for global routes (e.g. /search) that carry no repo
// segment at all, since every useApiResource caller still needs some value.
export function useCurrentRepo(): string {
  const { pathname } = useLocation();
  return parseRoute(pathname).repo ?? "atlas";
}
